'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Offer } from '@/services/dataTypes'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import { Star, ShoppingBag, ShoppingCart, Copy, Check, ShieldCheck, Tag, Clock, ChevronDown, ChevronRight, ExternalLink, FileText, CheckCircle2, Layers, Store, Zap } from 'lucide-react'
import { toast } from 'react-toastify'
import CommentSection from './CommentSection'
import EventOfferCard from './EventOfferCard'

interface Props {
    product: Offer
    merchantRes: any
    categories: any[]
    relatedProducts: any[]
    companyDomain: string
    companyId: string
    storeSlug: string
    slugType: string
    currentMerchantSlug: string
    categorySlug?: string
}

const ProductDetailInteractive = ({
    product,
    merchantRes,
    categories,
    relatedProducts,
    companyDomain,
    companyId,
    storeSlug,
    slugType,
    currentMerchantSlug,
    categorySlug
}: Props) => {
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const hasDiscount = originalPrice > 0 && salePrice > 0 && originalPrice > salePrice
    const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title, discountPercent)
    const savings = hasDiscount ? (originalPrice - salePrice).toFixed(2) : null
    const currencySymbol = getCurrencySymbol(product?.currency)

    const duration = calculateOfferDuration(product?.end_date)
    const isLifetime = !product?.end_date || duration === 'Life Time'

    const merchantName = product?.merchant?.merchant_name || merchantRes?.merchant_name || currentMerchantSlug
    const merchantLogo = product?.merchant?.merchant_logo || merchantRes?.merchant_logo
    const merchantHref = getMerchantHref(product?.merchant || merchantRes, storeSlug, slugType)
    const formattedCategory = product?.category?.name || 'Products'

    // Combine FAQs from product or merchant
    const allFaqs = Array.isArray(product?.faqs) && product.faqs.length > 0
        ? product.faqs
        : (Array.isArray(merchantRes?.faqs) ? merchantRes.faqs : [])

    const [openFaq, setOpenFaq] = useState<number | null>(null)

    // Product image
    const mainProductImage = getBaseImageUrl(companyDomain, product?.product_image || merchantLogo, '')

    // Interactive copy state
    const [copied, setCopied] = useState<boolean>(false)

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        try {
            await navigator.clipboard.writeText(product.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            try {
                const el = document.createElement('textarea')
                el.value = product.coupon_code
                el.style.position = 'fixed'
                el.style.opacity = '0'
                const container = document.body || document.documentElement
                if (container) {
                    container.appendChild(el)
                    el.focus()
                    el.select()
                    document.execCommand('copy')
                    container.removeChild(el)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                }
            } catch {
                toast.error('Failed to copy. Please copy manually.')
            }
        }
    }

    const cleanDetail = product?.offer_detail ? discardHTMLTags(product.offer_detail) : ''
    const ratingVal = 4.8

    return (
        <div className="min-h-screen pb-16" style={{ background: '#FAF7F2' }}>
            {/* ━━━ MAIN CONTAINER ━━━ */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-6 space-y-4">

                {/* ━━━ In-Page Breadcrumb ━━━ */}
                <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs font-medium text-slate-500 overflow-x-auto whitespace-nowrap scrollbar-hide py-1">
                    <Link href="/" className="hover:text-[#2D3E2D] transition-colors flex items-center gap-1 shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        Home
                    </Link>
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <Link href="/products" className="hover:text-[#2D3E2D] transition-colors shrink-0">Products</Link>
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <Link href={merchantHref} className="hover:text-[#2D3E2D] transition-colors shrink-0">{merchantName}</Link>
                    {product?.category?.slug && (
                        <>
                            <ChevronRight size={12} className="text-slate-300 shrink-0" />
                            <Link href={`/products/${currentMerchantSlug}/${product.category.slug}`} className="hover:text-[#2D3E2D] transition-colors shrink-0">
                                {formattedCategory}
                            </Link>
                        </>
                    )}
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <span className="text-[#2D3E2D] font-semibold max-w-[200px] sm:max-w-xs">
                        {discardHTMLTags(product?.offer_title)}
                    </span>
                </nav>

                {/* ━━━ HERO SHOWCASE GRID ━━━ */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start pt-1">

                    {/* LEFT COLUMN: Product Showcase Image Card */}
                    <div className="lg:col-span-5">
                        <div className="relative bg-white rounded-[2rem] border border-slate-200/80 p-4 sm:p-6 shadow-[0_4px_20px_rgba(45,62,45,0.06)] overflow-hidden flex items-center justify-center min-h-[250px] sm:min-h-[280px] group">
                            {/* Floating Discount Badge */}
                            {(finalDiscountTag || discountPercent) && (
                                <div className="absolute top-3.5 left-3.5 z-10">
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black text-[#2D3E2D] bg-[#E8F0E8] border border-[#5C7A5C]/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                                        <Tag size={10} className="text-[#5C7A5C]" /> {finalDiscountTag || `${discountPercent}% OFF`}
                                    </span>
                                </div>
                            )}

                            {/* Floating Duration / Expiry Badge */}
                            {duration && !isLifetime && (
                                <div className="absolute top-3.5 right-3.5 z-10">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FAF7F2] text-[#5C7A5C] border border-[#5C7A5C]/20 shadow-sm">
                                        <Clock size={11} className="text-[#5C7A5C]" /> {duration}
                                    </span>
                                </div>
                            )}

                            {/* Product Image with Zoom */}
                            <div className="relative w-full h-[190px] sm:h-[230px] flex items-center justify-center">
                                <Image
                                    src={mainProductImage}
                                    alt={discardHTMLTags(product.offer_title)}
                                    fill
                                    className="object-contain transition-transform duration-500 group-hover:scale-105"
                                    priority
                                    unoptimized
                                />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Product Title, Pricing & CTAs */}
                    <div className="lg:col-span-7 space-y-3.5">

                        {/* Top Badges & Product Title */}
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2D3E2D] bg-[#E8F0E8] px-2.5 py-0.5 rounded-full border border-[#5C7A5C]/20">
                                    <ShieldCheck size={11} className="text-[#5C7A5C]" /> Verified Deal
                                </span>
                                {product?.offer_type?.name && (
                                    <span className="text-[10px] font-semibold text-slate-500 bg-white px-2.5 py-0.5 rounded-full border border-slate-200 capitalize">
                                        {product.offer_type.name}
                                    </span>
                                )}
                            </div>

                            <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-[#2D3E2D] leading-snug tracking-tight max-w-xl"
                                style={{ fontFamily: 'Georgia, serif' }}>
                                {discardHTMLTags(product?.offer_title)}
                            </h1>
                        </div>

                        {/* Rating Row */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        size={14}
                                        className="text-[#F5C542] fill-[#F5C542]"
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-bold text-slate-700">{ratingVal}</span>
                        </div>

                        {/* ── Price Block ── */}
                        {(salePrice > 0 || originalPrice > 0) && (
                            <div className="space-y-2 pt-0.5">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    {salePrice > 0 ? (
                                        <span className="text-2xl sm:text-3xl font-black text-[#2D3E2D] leading-none tracking-tight">
                                            {currencySymbol}{salePrice.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-2xl sm:text-3xl font-black text-[#2D3E2D] leading-none tracking-tight">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {originalPrice > 0 && salePrice > 0 && (
                                        <span className="text-sm text-slate-400 line-through font-medium">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {discountPercent && discountPercent > 0 && (
                                        <span className="inline-flex items-center text-[10px] font-black text-[#2D3E2D] bg-[#E8F0E8] border border-[#5C7A5C]/20 px-2 py-0.5 rounded-full">
                                            {discountPercent}% OFF
                                        </span>
                                    )}
                                </div>

                                {/* Savings callout */}
                                {savings && (
                                    <div className="flex items-center gap-1.5 px-3 py-1 bg-[#E8F0E8]/70 border border-[#5C7A5C]/20 rounded-xl w-fit">
                                        <CheckCircle2 size={13} className="text-[#5C7A5C] shrink-0" />
                                        <span className="text-xs font-semibold text-[#2D3E2D]">
                                            You save <span className="font-black">{currencySymbol}{savings}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons: Buy Now & Visit Store */}
                        <div className="flex items-center flex-wrap gap-2.5 pt-1">
                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit"
                            >
                                <div
                                    className="w-fit inline-flex items-center justify-center gap-1.5 px-6 h-11 text-[#2D3E2D] font-bold text-xs uppercase tracking-wider rounded-xl transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 cursor-pointer shrink-0"
                                    style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}
                                >
                                    <ShoppingBag size={14} />
                                    <span>{product?.coupon_code ? 'Get Code & Buy Now' : 'Buy Now'}</span>
                                </div>
                            </OfferOutUrl>

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit"
                            >
                                <div
                                    className="w-fit inline-flex items-center justify-center gap-1.5 px-5 h-11 bg-white hover:bg-[#FAF7F2] text-[#2D3E2D] border border-slate-200 font-bold text-xs uppercase tracking-wider rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
                                >
                                    <ShoppingCart size={14} />
                                    <span>Visit Store</span>
                                </div>
                            </OfferOutUrl>
                        </div>

                        {/* Coupon Code Section (if present) */}
                        {product?.coupon_code && (
                            <div className="bg-white border border-dashed border-[#5C7A5C]/40 p-3 sm:p-3.5 rounded-2xl flex items-center justify-between gap-3 shadow-sm max-w-md">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#5C7A5C] flex items-center gap-1">
                                        <Tag size={11} /> Promo Code
                                    </span>
                                    <p className="text-base sm:text-lg font-mono font-black text-[#2D3E2D] tracking-wider mt-0.5 select-all">
                                        {product.coupon_code}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 ${copied
                                        ? 'bg-[#5C7A5C] text-white'
                                        : 'bg-[#2D3E2D] hover:bg-[#1E2B1E] text-white'
                                        }`}
                                >
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                                </button>
                            </div>
                        )}

                        {/* Trust & Guarantee Indicators */}
                        <div className="flex items-center gap-3 flex-wrap text-[10px] font-semibold text-slate-400 pt-1">
                            <span className="inline-flex items-center gap-1 text-[#5C7A5C] font-bold">
                                <ShieldCheck size={12} className="text-[#5C7A5C]" /> 100% Verified
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-slate-600 font-medium">
                                <Zap size={11} className="text-[#F5C542]" /> Direct Link
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-slate-600 font-medium">
                                <Clock size={11} className="text-slate-400" /> Daily Updated
                            </span>
                        </div>
                    </div>
                </div>

                {/* LOWER SECTION: Product Description, FAQs & Sidebar */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                    {/* ── LEFT COLUMN (lg:col-span-8): Description & FAQs ── */}
                    <div className="lg:col-span-8 space-y-6">
                        {/* Full Product Description (Complete, No Truncation) */}
                        {cleanDetail && (
                            <div className="bg-white border border-slate-200/80 rounded-[1.75rem] p-5 sm:p-6 shadow-sm space-y-3">
                                <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100">
                                    <FileText size={16} className="text-[#5C7A5C]" />
                                    <h2 className="text-xs font-bold uppercase tracking-wider text-[#2D3E2D]">
                                        Product Description & Overview
                                    </h2>
                                </div>

                                <div className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                    {product.offer_detail ? (
                                        <div
                                            className="prose prose-slate prose-xs sm:prose-sm max-w-none text-slate-600 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: product.offer_detail }}
                                        />
                                    ) : (
                                        <p>{cleanDetail}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* FAQs Section */}
                        {allFaqs.length > 0 && (
                            <div className="bg-white rounded-[1.75rem] border border-slate-200/80 p-5 sm:p-6 shadow-sm space-y-3">
                                <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#2D3E2D] flex items-center gap-1.5">
                                        Frequently Asked Questions
                                    </h3>
                                    <span className="text-[10px] font-bold text-[#5C7A5C] bg-[#E8F0E8] px-2.5 py-0.5 rounded-full">
                                        {allFaqs.length} FAQs
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {allFaqs.map((faq: any, idx: number) => (
                                        <details
                                            key={faq.id || idx}
                                            className="group border border-slate-200/80 rounded-xl p-3.5 transition-all duration-200 open:bg-[#FAF7F2]/50"
                                        >
                                            <summary className="text-xs font-bold text-slate-800 cursor-pointer list-none flex items-center justify-between gap-2">
                                                <span>{faq.question || faq.title}</span>
                                                <ChevronDown size={14} className="text-slate-400 group-open:rotate-180 transition-transform duration-200 shrink-0" />
                                            </summary>
                                            <div
                                                className="text-xs text-slate-600 mt-2.5 pt-2.5 border-t border-slate-200/60 leading-relaxed prose prose-slate prose-xs max-w-none"
                                                dangerouslySetInnerHTML={{ __html: faq.answer || faq.description || '' }}
                                            />
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── RIGHT COLUMN (lg:col-span-4): Sidebar (Store Profile, Categories, Reviews) ── */}
                    <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-6">
                        {/* 1. Official Store Quick Deal Card */}
                        <div className="bg-white rounded-[1.75rem] border border-slate-200/80 p-5 shadow-sm space-y-3.5">
                            <div className="flex items-center gap-3">
                                {merchantLogo && (
                                    <div className="w-12 h-12 rounded-2xl bg-[#FAF7F2] border border-slate-200/80 p-1.5 flex items-center justify-center shrink-0 shadow-2xs">
                                        <Image
                                            src={getBaseImageUrl(companyDomain, merchantLogo, '')}
                                            alt={merchantName}
                                            width={40}
                                            height={40}
                                            className="object-contain"
                                            unoptimized
                                        />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <p className="text-xs font-bold text-[#2D3E2D] truncate">{merchantName}</p>
                                        <ShieldCheck size={12} className="text-[#5C7A5C] shrink-0" />
                                    </div>
                                    <p className="text-[10px] text-slate-400">Official Store Partner</p>
                                </div>
                            </div>

                            <div className="h-px bg-slate-100" />

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-full"
                            >
                                <div
                                    className="w-full flex items-center justify-center gap-1.5 text-white font-bold text-[11px] uppercase tracking-wider py-2.5 rounded-xl transition-all shadow-sm active:scale-[0.98] cursor-pointer hover:bg-[#1E2B1E]"
                                    style={{ background: '#2D3E2D' }}
                                >
                                    <Store size={13} />
                                    <span>Visit Official Store</span>
                                </div>
                            </OfferOutUrl>

                            <Link
                                href={merchantHref}
                                className="block text-center text-[11px] font-semibold text-slate-500 hover:text-[#2D3E2D] hover:underline transition-colors"
                            >
                                View all {merchantName} coupons & deals →
                            </Link>
                        </div>

                        {/* 2. Shop by Category / Related Categories Card */}
                        {Array.isArray(categories) && categories.length > 0 && (
                            <div className="bg-white rounded-[1.75rem] border border-slate-200/80 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 border-b border-slate-100 bg-[#FAF7F2] flex items-center gap-1.5">
                                    <Layers size={13} className="text-[#5C7A5C]" />
                                    <h3 className="text-[11px] font-bold text-[#2D3E2D] uppercase tracking-wider">
                                        Shop by Category
                                    </h3>
                                </div>
                                <div className="p-2 divide-y divide-slate-50">
                                    {categories.slice(0, 8).map((cat: any, i: number) => (
                                        <Link
                                            key={cat.slug || i}
                                            href={`/products/${currentMerchantSlug}/${cat.slug}`}
                                            className="flex items-center justify-between py-2 px-2.5 rounded-xl hover:bg-[#FAF7F2] group transition-colors"
                                        >
                                            <span className="text-[11px] font-medium text-slate-600 group-hover:text-[#2D3E2D] transition-colors truncate">
                                                {cat.name}
                                            </span>
                                            <ChevronRight size={13} className="text-slate-300 group-hover:text-[#5C7A5C] transition-colors shrink-0" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 3. Customer Reviews & Comment Section */}
                        <div id="reviews-section" className="bg-white rounded-[1.75rem] border border-slate-200/80 p-5 shadow-sm">
                            <CommentSection
                                offer_id={product?.unique_id || ''}
                                company_id={companyId}
                            />
                        </div>
                    </div>
                </div>

                {/* SIMILAR PRODUCTS (Same Category) */}
                {relatedProducts?.length > 0 && (
                    <div className="mt-12 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-base sm:text-lg font-bold text-[#2D3E2D] tracking-tight"
                                    style={{ fontFamily: 'Georgia, serif' }}>
                                    Similar Deals in <span className="text-[#5C7A5C]">{formattedCategory}</span>
                                </h2>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Handpicked top deals and discounts
                                </p>
                            </div>
                            {product?.category?.slug && (
                                <Link
                                    href={`/products/${currentMerchantSlug}/${product.category.slug}`}
                                    className="text-xs font-bold text-[#5C7A5C] hover:underline flex items-center gap-1"
                                >
                                    <span>View all</span>
                                    <ExternalLink size={12} />
                                </Link>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {relatedProducts.slice(0, 4).map((item: any, i: number) => (
                                <EventOfferCard
                                    key={i}
                                    product={item}
                                    merchantHref={getMerchantHref(merchantRes, storeSlug, slugType)}
                                    domain={companyDomain}
                                    merchant_name={merchantRes?.merchant_name}
                                    merchant_logo={merchantRes?.merchant_logo}
                                    productDetailUrl={getProductDetailHref(merchantRes, slugType, item?.slug)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductDetailInteractive
